import { MouseEvent, useCallback, useMemo, PropsWithChildren } from 'react';
import classNames from 'classnames';
import { CollapsibleCard } from '../../../DesignSystems/Collapse/CollapsibleCard';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { StylishNewCheckbox } from '../../../DesignSystems/New/StylishNewCheckbox';
import { SharedIcon } from '../../../SharedIcon/SharedIcon';
import { SECTOR, type AORHierarchyNode } from './utils/schema';
import { useUscgMapContext } from '../LayersTab/UscgMapContext';
import { GetSitrepsResponse } from 'components/Reports/reports.types';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import SitrepToggleSection from 'components/Reports/SitrepToggleSection';
import { useAorSitrep } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { useAutoZoomToHomeAor } from './utils/useAutoZoomToHomeAor';

type HierarchyItemCardActionsProps = {
  name: string;
  onGoTo: (e: MouseEvent) => void;
};

const HierarchyItemCardActions = ({
  name,
  onGoTo,
}: HierarchyItemCardActionsProps) => (
  <StylishNewButton
    className="button--icon"
    title="Go to location"
    aria-label={`Go to ${name} location`}
    onClick={onGoTo}
  >
    <SharedIcon iconName="map" size="sm" />
  </StylishNewButton>
);

type HierarchyItemCardBodyProps = {
  aorSitrep: GetSitrepsResponse | null;
  onAction?: () => void;
  actionLabel?: string;
  noActionMessage?: string;
  children?: React.ReactNode;
};

const HierarchyItemCardBody = ({
  aorSitrep,
  onAction,
  actionLabel,
  noActionMessage,
  children,
}: PropsWithChildren<HierarchyItemCardBodyProps>) => (
  <>
    {aorSitrep && (
      <SitrepToggleSection>
        <CurrentReportsSitrepCard sitrep={aorSitrep} />
      </SitrepToggleSection>
    )}
    {onAction && actionLabel ? (
      <StylishNewButton className="button--secondary mt-2" onClick={onAction}>
        {actionLabel}
      </StylishNewButton>
    ) : noActionMessage ? (
      <div className="small text-muted mt-2">{noActionMessage}</div>
    ) : null}
    {children}
  </>
);

type HierarchyItemCardProps = {
  item: AORHierarchyNode;
  sectionKey: string;
  allAorIds: string[];
  level?: string;
  /** IDs of all descendant AOR items (children, grandchildren, etc.) */
  descendantIds?: string[];
  onAction?: () => void;
  actionLabel?: string;
  noActionMessage?: string;
  marginClass?: string;
};

export const HierarchyItemCard = ({
  item,
  sectionKey,
  allAorIds,
  level,
  descendantIds,
  onAction,
  actionLabel,
  noActionMessage,
  marginClass = 'mb-2',
  children,
}: PropsWithChildren<HierarchyItemCardProps>) => {
  const {
    aorFilterIds,
    setAorFilterIds,
    isAorVisible,
    toggleAorVisibility,
    isMapVisible,
  } = useUscgMapContext();
  const { zoomToAorById } = useAutoZoomToHomeAor();

  const selfAndDescendantIds = useMemo(
    () => [item.id, ...(descendantIds ?? [])],
    [item.id, descendantIds]
  );

  const isIdVisible = useCallback(
    (id: string) => {
      if (!isAorVisible) return false;
      if (!aorFilterIds || aorFilterIds.length === 0) return true;
      return aorFilterIds.includes(id);
    },
    [aorFilterIds, isAorVisible]
  );

  const isAllVisible = useMemo(
    () => selfAndDescendantIds.every(isIdVisible),
    [selfAndDescendantIds, isIdVisible]
  );

  const isIndeterminate = useMemo(
    () => !isAllVisible && selfAndDescendantIds.some(isIdVisible),
    [isAllVisible, selfAndDescendantIds, isIdVisible]
  );

  const itemName = useMemo(
    () => (level && level === SECTOR ? `Sector ${item.name}` : item.name),
    [item.name, level]
  );

  const { data: aorSitrep } = useAorSitrep(item.id);

  const handleToggleVisibility = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      if (isAllVisible) {
        // Hide all: keep only IDs not in selfAndDescendantIds
        const currentlyVisible =
          aorFilterIds && aorFilterIds.length > 0 ? aorFilterIds : allAorIds;
        const nextVisibleIds = currentlyVisible.filter(
          (id) => !selfAndDescendantIds.includes(id)
        );

        if (nextVisibleIds.length === 0) {
          setAorFilterIds([]);
          if (isAorVisible) toggleAorVisibility();
          return;
        }

        setAorFilterIds(nextVisibleIds);
        return;
      }

      // Show all (covers both "none visible" and "indeterminate" states)
      if (!isAorVisible) {
        toggleAorVisibility();
        setAorFilterIds(selfAndDescendantIds);
        return;
      }

      if (!aorFilterIds || aorFilterIds.length === 0) {
        // Everything is already visible; nothing to change
        return;
      }

      setAorFilterIds([...new Set([...aorFilterIds, ...selfAndDescendantIds])]);
    },
    [
      allAorIds,
      aorFilterIds,
      isAllVisible,
      isAorVisible,
      selfAndDescendantIds,
      setAorFilterIds,
      toggleAorVisibility,
    ]
  );

  const handleGoTo = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      if (!item.currentLocationGeom) return;

      try {
        if (!isAllVisible) handleToggleVisibility(e);
        await zoomToAorById(item.id, {
          filterIds: selfAndDescendantIds,
          ensureVisible: true,
        });
      } catch (error) {
        console.error('Error zooming to AOR:', error);
        return;
      }
    },
    [
      handleToggleVisibility,
      isAllVisible,
      item.id,
      item.currentLocationGeom,
      selfAndDescendantIds,
      zoomToAorById,
    ]
  );

  const hasContent = Boolean(
    item.description || onAction || noActionMessage || children || aorSitrep
  );

  if (!hasContent) {
    return (
      <div
        className={classNames(
          'card-outlined',
          'd-flex justify-content-between align-items-center p-3',
          marginClass
        )}
      >
        <div className="d-flex align-items-center gap-2">
          {isMapVisible && (
            <StylishNewCheckbox
              className="mt-0"
              checked={isAllVisible}
              indeterminate={isIndeterminate}
              onClick={handleToggleVisibility}
              aria-label={
                isAllVisible
                  ? `Hide ${itemName} from map`
                  : `Show ${itemName} on map`
              }
            />
          )}
          <span>{itemName}</span>
        </div>
        {isMapVisible && (
          <HierarchyItemCardActions name={itemName} onGoTo={handleGoTo} />
        )}
      </div>
    );
  }

  return (
    <CollapsibleCard
      sectionKey={sectionKey}
      headerLeft={
        <div className="d-flex align-items-center gap-2">
          {isMapVisible && (
            <StylishNewCheckbox
              className="mt-0"
              checked={isAllVisible}
              indeterminate={isIndeterminate}
              onClick={handleToggleVisibility}
              aria-label={
                isAllVisible
                  ? `Hide ${itemName} from map`
                  : `Show ${itemName} on map`
              }
            />
          )}
          <span>{itemName}</span>
        </div>
      }
      headerRight={
        isMapVisible && (
          <HierarchyItemCardActions name={itemName} onGoTo={handleGoTo} />
        )
      }
      className={classNames('card-outlined', marginClass)}
      contentClassName="p-2"
      defaultCollapsed
      arrowPosition="left"
    >
      <HierarchyItemCardBody
        aorSitrep={aorSitrep}
        onAction={onAction}
        actionLabel={actionLabel}
        noActionMessage={noActionMessage}
      >
        {children}
      </HierarchyItemCardBody>
    </CollapsibleCard>
  );
};
